import type { DashboardLanguage, DashboardLanguagePreference } from "../types";

export const DASHBOARD_LANGUAGE_STORAGE_KEY = "appLanguage";
export const DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY = "appLanguagePreference";
export const DASHBOARD_LANGUAGE_CHANGE_EVENT = "dashboard-language-change";
export const DEFAULT_DASHBOARD_LANGUAGE: DashboardLanguage = "en";
export const SUPPORTED_DASHBOARD_LANGUAGES = ["da", "en"] as const;

export function normalizeDashboardLanguage(value: unknown): DashboardLanguage | null {
  const baseLanguage = String(value || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];

  return (SUPPORTED_DASHBOARD_LANGUAGES as readonly string[]).includes(baseLanguage)
    ? (baseLanguage as DashboardLanguage)
    : null;
}

export function normalizeDashboardLanguagePreference(
  value: unknown,
): DashboardLanguagePreference | null {
  return normalizeDashboardLanguage(value);
}

function readIntlLocale(intlObject: typeof Intl | undefined): string {
  try {
    return intlObject?.DateTimeFormat?.().resolvedOptions?.().locale || "";
  } catch {
    return "";
  }
}

export function detectDashboardLanguage({
  navigatorObject,
  intlObject,
}: {
  navigatorObject?: Navigator;
  intlObject?: typeof Intl;
} = {}): DashboardLanguage {
  const resolvedNavigator =
    navigatorObject || (typeof navigator !== "undefined" ? navigator : undefined);
  const resolvedIntl = intlObject || (typeof Intl !== "undefined" ? Intl : undefined);
  const candidates = [
    ...(Array.isArray(resolvedNavigator?.languages) ? resolvedNavigator.languages : []),
    resolvedNavigator?.language,
    readIntlLocale(resolvedIntl),
  ];

  for (const candidate of candidates) {
    const language = normalizeDashboardLanguage(candidate);
    if (language) {
      return language;
    }
  }

  return DEFAULT_DASHBOARD_LANGUAGE;
}

export function readDashboardLanguagePreference(
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  {
    navigatorObject,
    intlObject,
  }: {
    navigatorObject?: Navigator;
    intlObject?: typeof Intl;
  } = {},
): DashboardLanguagePreference {
  try {
    const rawPreference = storage?.getItem(DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY);
    if (rawPreference === "system") {
      return detectDashboardLanguage({ navigatorObject, intlObject });
    }

    const storedPreference = normalizeDashboardLanguagePreference(rawPreference);
    if (storedPreference) {
      return storedPreference;
    }

    const legacyLanguage = normalizeDashboardLanguage(
      storage?.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY),
    );
    if (legacyLanguage) {
      return legacyLanguage;
    }
  } catch {
    // Fall through to default handling.
  }

  return detectDashboardLanguage({ navigatorObject, intlObject });
}

export function resolveDashboardLanguage(
  preference: DashboardLanguagePreference,
): DashboardLanguage {
  return preference;
}

export function persistDashboardLanguagePreference(
  preference: DashboardLanguagePreference,
  resolvedLanguage: DashboardLanguage = preference,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
): void {
  try {
    storage?.setItem(DASHBOARD_LANGUAGE_PREFERENCE_STORAGE_KEY, preference);
    storage?.setItem(DASHBOARD_LANGUAGE_STORAGE_KEY, resolvedLanguage);
  } catch {
    // Persisting is best-effort (e.g. storage disabled).
  }
}

/** @deprecated Use DashboardLanguageProvider instead. Kept for legacy callers/tests. */
export function readDashboardLanguage(
  storage = typeof localStorage !== "undefined" ? localStorage : null,
): DashboardLanguage | null {
  try {
    return normalizeDashboardLanguage(storage?.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** @deprecated Use DashboardLanguageProvider instead. Kept for legacy callers/tests. */
export function initializeDashboardLanguage({
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  navigatorObject,
  intlObject,
}: {
  storage?: Storage | null;
  navigatorObject?: Navigator;
  intlObject?: typeof Intl;
} = {}): DashboardLanguage {
  const preference = readDashboardLanguagePreference(storage, { navigatorObject, intlObject });
  persistDashboardLanguagePreference(preference, preference, storage);
  return preference;
}
